import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const formatApiError = (detail) => {
  if (!detail) return "Une erreur est survenue";
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  }
  return String(detail);
};

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Connexion réussie');
      navigate('/app');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative bg-zinc-950 overflow-hidden">
      {/* Glow doré assorti à la landing */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(245,176,22,0.18),transparent_50%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_80%,rgba(245,176,22,0.08),transparent_60%)] pointer-events-none" />

      <Card
        className="w-full max-w-md relative z-10 bg-zinc-900/80 backdrop-blur-xl border-amber-500/20 shadow-2xl shadow-amber-500/5"
        data-testid="login-card"
      >
        <CardHeader className="text-center pt-8">
          <Link to="/" className="mx-auto mb-3 block w-24 h-24" data-testid="login-logo">
            <img src="/brand/logo.png" alt="Home MDB 2A" className="w-full h-full object-contain drop-shadow-[0_0_20px_rgba(245,176,22,0.25)]" />
          </Link>
          <div className="font-bold text-lg tracking-tight text-zinc-100 mb-1">
            Home <span className="text-amber-400">MDB 2A</span>
          </div>
          <CardTitle className="text-2xl font-bold text-white font-['Outfit'] mt-3">Connexion</CardTitle>
          <CardDescription className="text-zinc-400">
            Accède à ton espace Marchand de Biens
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="ton@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-600 focus-visible:ring-amber-500/40 focus-visible:border-amber-500/50"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">Mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-600 pr-10 focus-visible:ring-amber-500/40 focus-visible:border-amber-500/50"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-amber-400 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold transition-all hover:shadow-lg hover:shadow-amber-500/30"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Se connecter
            </Button>
          </form>
          <p className="mt-6 text-center text-sm">
            <Link to="/forgot-password" className="text-zinc-400 hover:text-amber-400 transition-colors">
              Mot de passe oublié ?
            </Link>
          </p>
          <p className="mt-3 text-center text-xs text-zinc-600">
            Pas encore de compte ?{' '}
            <Link to="/signup" className="text-amber-400 hover:text-amber-300 font-medium" data-testid="login-to-signup">
              Démarrer un essai gratuit
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
